import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, Play, RefreshCw, RotateCcw, Sliders, StopCircle, TestTube } from "lucide-react";
import { Slider } from "../components/ui/slider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v, d = 2) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });

const PnlPill = ({ value }) => {
  if (value == null) return <span className="text-muted-3">—</span>;
  const pos = value >= 0;
  return (
    <span className="font-mono-data" style={{ color: pos ? "var(--bull)" : "var(--bear)" }}>
      {pos ? "+" : ""}{fmt(value)}%
    </span>
  );
};

const CloseReasonBadge = ({ reason }) => {
  const map = {
    PROFIT_BOOKED: { l: "BOOKED", c: "var(--bull)" },
    STOP_LOSS: { l: "STOP", c: "var(--bear)" },
    HARD_EXIT: { l: "HARD", c: "#FFB66E" },
    EOD: { l: "EOD", c: "#FFB66E" },
    MANUAL_EXIT: { l: "MANUAL", c: "#8FB8FF" },
    // VWAP+OI staged-exit reasons (2026-08-30 — see vwap_oi_backtest.py)
    TARGET_1R: { l: "T1", c: "var(--bull)" },
    TARGET_2R: { l: "T2", c: "var(--bull)" },
    BREAKEVEN_STOP: { l: "B/E", c: "#FFB66E" },
    TRAIL_EMA_EXIT: { l: "TRAIL", c: "#8FB8FF" },
  };
  const cfg = map[reason] || { l: reason || "—", c: "var(--text-muted)" };
  return (
    <span className="inline-block px-1.5 py-0.5 border font-mono-data text-[10px]"
          style={{ color: cfg.c, borderColor: cfg.c }}>
      {cfg.l}
    </span>
  );
};

// Shared background-job progress/cancel widget — same shape as the
// job_engine dispatch LinearAccumulation.jsx already uses; reused here for
// the Candle Breakout + Sniper backtests (2026-09-12) since both now always
// run as background jobs to avoid the reverse-proxy request timeout.
const JobProgress = ({ job, onCancel, testPrefix }) => {
  if (!job) return null;
  return (
    <div className="pt-3 border-t border-grid space-y-2" data-testid={`${testPrefix}-job`}>
      <div className="flex items-center justify-between gap-2 text-[11px] font-mono-data">
        <div className="flex items-center gap-2">
          <span className="text-muted-3 text-[10px] uppercase tracking-wider">Job</span>
          <span className="font-bold">{job.job_id}</span>
          <span
            className="text-[9px] uppercase px-2 py-0.5 border"
            style={{
              color: job.status === "running" ? "var(--warn, #d19a66)"
                   : job.status === "success" ? "var(--bull)"
                   : job.status === "failed" ? "var(--bear)"
                   : "var(--muted-3)",
              borderColor: "currentColor",
            }}
          >
            {job.status}
          </span>
        </div>
        {["queued", "running"].includes(job.status) && (
          <button
            onClick={onCancel}
            data-testid={`${testPrefix}-cancel`}
            className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase font-mono-data"
            style={{ color: "var(--bear)", borderColor: "var(--bear)" }}
          >
            <StopCircle size={12} /> Cancel
          </button>
        )}
      </div>
      <div className="w-full h-2 bg-white/[0.05] border border-grid overflow-hidden">
        <div
          className="h-full"
          style={{
            width: `${job.progress || 0}%`,
            backgroundColor: "var(--bull)",
            transition: "width 0.5s ease",
          }}
          data-testid={`${testPrefix}-progress`}
        />
      </div>
      <div className="flex justify-between text-[10px] text-muted-3 font-mono-data">
        <span>{(job.progress || 0).toFixed(1)}%</span>
        <span>
          {job.eta_seconds != null ? `ETA ${Math.max(0, Math.round(job.eta_seconds))} s` : "…"}
        </span>
      </div>
    </div>
  );
};

const SummaryGrid = ({ summary }) => (
  <div className="grid grid-cols-2 md:grid-cols-6 gap-2 font-mono-data text-[11px]">
    {[
      ["Trades", summary?.total_trades ?? 0, null],
      ["Wins", summary?.wins ?? 0, "var(--bull)"],
      ["Losses", summary?.losses ?? 0, "var(--bear)"],
      ["Win rate", `${summary?.win_rate_pct ?? 0}%`, null],
      ["Gross P&L", `${(summary?.gross_pnl_pct ?? 0) >= 0 ? "+" : ""}${fmt(summary?.gross_pnl_pct)}%`,
        (summary?.gross_pnl_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)"],
      ["Avg P&L", `${(summary?.avg_pnl_pct ?? 0) >= 0 ? "+" : ""}${fmt(summary?.avg_pnl_pct)}%`, null],
    ].map(([k, v, color]) => (
      <div key={k} className="border border-grid p-2">
        <div className="text-muted-3 uppercase text-[9px] tracking-widest">{k}</div>
        <div className="mt-0.5" style={color ? { color } : {}}>{v}</div>
      </div>
    ))}
  </div>
);

const FilterEditor = ({ fields, values, onChange }) => (
  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 font-mono-data text-[11px]">
    {fields.map(([k, label, step]) => (
      <div key={k} className="flex flex-col gap-1">
        <span className="text-muted-3 text-[9px] uppercase tracking-widest">{label}</span>
        <input type="number" step={step} value={values[k] ?? 0}
                onChange={(e) => onChange(k, parseFloat(e.target.value))}
                data-testid={`bt-filter-${k}`}
                className="bg-transparent border border-grid px-2 py-1 text-xs" />
      </div>
    ))}
  </div>
);

// Human-friendly label + step-size heuristic for auto-derived editor rows.
// Callers only need to pass a defaults dict — new admin keys surface in the
// UI automatically with sensible labels/steps.
const UPPERCASE_TOKENS = ["rvol", "atr", "rsi", "vwap", "oi", "ce", "pe",
                           "ema", "mtf", "sl", "tp", "iv"];

const humanizeLabel = (key) => {
  let s = key.replace(/_/g, " ").replace(/\bpct\b/gi, "%");
  for (const t of UPPERCASE_TOKENS) {
    s = s.replace(new RegExp(`\\b${t}\\b`, "gi"), t.toUpperCase());
  }
  s = s.replace(/\bmax\b/gi, "Max").replace(/\bmin\b/gi, "Min");
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
};

const inferStep = (key, value) => {
  const k = key.toLowerCase();
  if (k.endsWith("_enabled") || k.includes("lookback") || k.includes("per_day") ||
      k.includes("_min") || k.includes("_max")) return 1;
  if (Number.isInteger(value)) return 1;
  if (k.includes("pct") || k.includes("threshold") || k.includes("multiplier") ||
      k.includes("ratio") || k.includes("buffer") || k.includes("distance")) return 0.05;
  return 0.05;
};

const deriveFields = (defaults) =>
  Object.entries(defaults || {}).map(([k, v]) => [k, humanizeLabel(k), inferStep(k, v)]);


const TradesTable = ({ trades, showSymbol = false, showSetup = false }) => {
  if (!trades?.length) return null;
  return (
    <div className="border border-grid overflow-x-auto max-h-96">
      <table className="w-full text-xs font-mono-data">
        <thead className="sticky top-0 bg-black">
          <tr className="border-b border-grid text-muted-3 uppercase text-[9px] tracking-widest">
            <th className="px-3 py-2 text-left">Day</th>
            {showSymbol && <th className="px-3 py-2 text-left">Symbol</th>}
            {showSetup && <th className="px-3 py-2 text-left">Setup</th>}
            <th className="px-3 py-2 text-left">Entry</th>
            <th className="px-3 py-2 text-left">Exit</th>
            <th className="px-3 py-2 text-left">Dir</th>
            <th className="px-3 py-2 text-right">Entry Spot</th>
            <th className="px-3 py-2 text-right">Exit Spot</th>
            <th className="px-3 py-2 text-right">P&L</th>
            <th className="px-3 py-2 text-center">Reason</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((t, i) => (
            <tr key={i} className="border-b border-grid">
              <td className="px-3 py-1.5">{t.day}</td>
              {showSymbol && <td className="px-3 py-1.5">{t.symbol}</td>}
              {showSetup && <td className="px-3 py-1.5">{t.setup}</td>}
              <td className="px-3 py-1.5 text-muted-2">{(t.entry_ist || "").split("T")[1]?.slice(0,5) || (t.entry_ist||"").split(" ")[1]}</td>
              <td className="px-3 py-1.5 text-muted-2">{(t.exit_ist || "").split("T")[1]?.slice(0,5) || (t.exit_ist||"").split(" ")[1]}</td>
              <td className="px-3 py-1.5">
                <span style={{ color: t.direction === "BULL" ? "var(--bull)" : "var(--bear)" }}>
                  {t.direction}
                </span>
              </td>
              <td className="px-3 py-1.5 text-right">{fmt(t.entry_spot, 0)}</td>
              <td className="px-3 py-1.5 text-right">{fmt(t.exit_spot, 0)}</td>
              <td className="px-3 py-1.5 text-right"><PnlPill value={t.pnl_pct} /></td>
              <td className="px-3 py-1.5 text-center"><CloseReasonBadge reason={t.close_reason} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// --- Individual backtest tabs ---

// Setup E V2 backtest — multi-market with per-market threshold overrides
const SETUP_E_V2_MARKETS = ["NIFTY", "BANKNIFTY", "SENSEX"];
const SETUP_E_V2_FIELDS = [
  ["score_threshold", "Score ≥", 1],
  ["premium_expansion_pct", "Premium Exp %", 1],
  ["rvol_threshold", "RVOL ≥", 0.05],
  ["velocity_threshold", "Velocity ≥", 0.05],
];

const SetupEBacktest = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDay, setFromDay] = useState(new Date(Date.now() - 90 * 86400e3).toISOString().slice(0, 10));
  const [toDay, setToDay] = useState(today);
  const [selectedMarkets, setSelectedMarkets] = useState(["NIFTY", "BANKNIFTY", "SENSEX"]);
  const [useOverride, setUseOverride] = useState(false);
  const [overrides, setOverrides] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/setup-e/today`).then((r) => {
      const seed = {};
      for (const k of SETUP_E_V2_MARKETS) {
        const m = r.data?.markets?.[k];
        if (m) {
          seed[k] = {
            score_threshold: m.score_threshold,
            premium_expansion_pct: m.premium_expansion_pct,
            rvol_threshold: m.rvol_threshold,
            velocity_threshold: m.velocity_threshold,
          };
        }
      }
      setOverrides(seed);
    }).catch(() => toast.error("Could not load Setup E V2 defaults"));
  }, []);

  const run = async () => {
    setRunning(true);
    try {
      const body = { from_day: fromDay, to_day: toDay, markets: selectedMarkets };
      if (useOverride && overrides) body.overrides = overrides;
      const r = await axios.post(`${API}/setup-e/backtest`, body);
      setResult(r.data);
    } catch (e) {
      const msg = e?.response?.status === 429
        ? (e.response.data?.detail || "Rate limited — please wait.")
        : "Backtest failed";
      toast.error(msg);
    } finally { setRunning(false); }
  };

  const toggleMarket = (mk) => {
    setSelectedMarkets((cur) => cur.includes(mk) ? cur.filter((x) => x !== mk) : [...cur, mk]);
  };

  const updateOverride = (mk, field, value) => {
    setOverrides((cur) => ({ ...cur, [mk]: { ...(cur?.[mk] || {}), [field]: value } }));
  };

  return (
    <div className="space-y-4" data-testid="bt-setup-e">
      <div className="text-[10px] text-muted-3 font-mono-data">
        Unified Expiry Momentum Engine V2. Replays 5-min spot candles across selected weekly expiries.
        OI + liquidity/premium filters skipped (historical option data unavailable) — option premium is
        projected from spot moves × delta ≈ 0.5.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs font-mono-data">
        <label className="flex flex-col gap-1">
          <span className="text-muted-3 text-[9px] uppercase tracking-widest">From</span>
          <input type="date" value={fromDay} onChange={(e) => setFromDay(e.target.value)}
                  data-testid="bt-setup-e-from" className="bg-transparent border border-grid px-2 py-1" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted-3 text-[9px] uppercase tracking-widest">To</span>
          <input type="date" value={toDay} onChange={(e) => setToDay(e.target.value)}
                  data-testid="bt-setup-e-to" className="bg-transparent border border-grid px-2 py-1" />
        </label>
        <div className="flex flex-col gap-1">
          <span className="text-muted-3 text-[9px] uppercase tracking-widest">Markets</span>
          <div className="flex gap-1">
            {SETUP_E_V2_MARKETS.map((mk) => (
              <button key={mk} onClick={() => toggleMarket(mk)}
                      data-testid={`bt-setup-e-market-${mk.toLowerCase()}`}
                      className="px-2 py-1 border font-mono-data text-[10px] uppercase"
                      style={{
                        color: selectedMarkets.includes(mk) ? "var(--bull)" : "var(--text-muted)",
                        borderColor: selectedMarkets.includes(mk) ? "var(--bull)" : "var(--text-muted)",
                      }}>
                {mk}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-end">
          <button onClick={run} disabled={running || selectedMarkets.length === 0}
                  data-testid="bt-setup-e-run"
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 border border-bull text-xs uppercase font-mono-data hover:bg-white/[0.03]"
                  style={{ color: "var(--bull)" }}>
            {running ? <RefreshCw size={12} className="animate-spin" /> : <Play size={12} />}
            {running ? "Running" : "Run Backtest"}
          </button>
        </div>
      </div>

      <label className="inline-flex items-center gap-2 text-[10px] font-mono-data cursor-pointer">
        <input type="checkbox" checked={useOverride} onChange={(e) => setUseOverride(e.target.checked)}
                data-testid="bt-setup-e-override-toggle" />
        <span>Override per-market thresholds</span>
      </label>

      {useOverride && overrides && (
        <div className="space-y-2">
          {SETUP_E_V2_MARKETS.map((mk) => (
            <div key={mk} className="border border-grid p-2">
              <div className="text-xs font-mono-data text-primary mb-1">{mk}</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono-data text-[11px]">
                {SETUP_E_V2_FIELDS.map(([f, label, step]) => (
                  <label key={f} className="flex flex-col gap-1">
                    <span className="text-muted-3 text-[9px] uppercase tracking-widest">{label}</span>
                    <input type="number" step={step}
                            value={overrides[mk]?.[f] ?? 0}
                            onChange={(e) => updateOverride(mk, f, parseFloat(e.target.value))}
                            data-testid={`bt-setup-e-${mk.toLowerCase()}-${f}`}
                            className="bg-transparent border border-grid px-2 py-1 text-xs" />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {result && (<>
        <SummaryGrid summary={result.summary} />
        {result.per_market && Object.keys(result.per_market).length > 0 && (
          <div className="border border-grid p-3 font-mono-data text-[10px]">
            <div className="text-muted-3 uppercase text-[9px] tracking-widest mb-1">Per-market breakdown</div>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {Object.entries(result.per_market).map(([mk, v]) => (
                <span key={mk} data-testid={`bt-setup-e-per-market-${mk.toLowerCase()}`}>
                  <span className="text-primary">{mk}</span>: {v.trades}T · {v.win_rate_pct}%WR ·
                  <span style={{ color: v.gross_pnl_pct >= 0 ? "var(--bull)" : "var(--bear)" }}>
                    {" "}{v.gross_pnl_pct >= 0 ? "+" : ""}{v.gross_pnl_pct}%
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
        {result.trades?.length ? <TradesTable trades={result.trades} showSymbol={false} /> :
          <div className="text-[10px] text-muted-3 font-mono-data">
            No signals. Scanned {result.expiry_days_scanned} expiries, got candles for {result.expiry_days_with_data}. Try loosening thresholds.
          </div>}
      </>)}
    </div>
  );
};

const VwapBounceBacktest = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDay, setFromDay] = useState(new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10));
  const [toDay, setToDay] = useState(today);
  const [useOverride, setUseOverride] = useState(false);
  const [override, setOverride] = useState(null);
  const [applyV2, setApplyV2] = useState(false);          // Phase 53.6 — v2 opt-in
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Phase 52c — hydrate override defaults from backend so JSX doesn't hardcode thresholds
  useEffect(() => {
    axios.get(`${API}/vwap-bounce/defaults`).then((r) => {
      if (r.data?.defaults) setOverride({ ...r.data.defaults });
    }).catch(() => {
      // Fallback if endpoint fails — matches backend DEFAULT_FILTERS
      setOverride({
        atr_distance_multiplier: 0.5, rvol_threshold: 1.75, rvol_lookback: 10,
        rsi_low: 55, rsi_high: 70, target_pct: 10, stop_pct: 15,
        setup_a_enabled: 1, setup_c_enabled: 1, max_entries_per_day: 3,
      });
    });
  }, []);

  const V2_ALL_ON = {
    // Setup B v2
    upper_wick_enabled: 1, upper_wick_max_ratio: 0.25,
    rsi_rising_enabled: 1, rsi_period: 14, rsi_floor_intraday: 55.0,
    vwap_slope_enabled: 1, vwap_slope_lookback_bars: 6, vwap_slope_min_pct: 0.05,
    session_high_check_enabled: 1, session_high_proximity_pct: 0.3, session_prior_pullback_pct: 0.5,
    strict_volume_rising: 1,
    htf_ma_slope_enabled: 1, htf_ma_period: 20, htf_ma_lookback_bars: 3, htf_ma_min_slope_pct: 0.03,
    // Setup C v2
    lower_wick_enabled: 1, lower_wick_max_ratio: 0.25,
    rsi_falling_enabled: 1, rsi_ceiling_intraday: 50.0,
    vwap_slope_down_enabled: 1, vwap_slope_max_pct: -0.05,
    session_low_check_enabled: 1, session_low_proximity_pct: 0.3, session_prior_bounce_pct: 0.5,
    htf_ma_slope_down_enabled: 1, htf_ma_max_slope_pct: -0.03,
  };

  const run = async () => {
    setRunning(true); setResult(null);
    try {
      const body = { from_day: fromDay, to_day: toDay };
      if (useOverride) body.filters = override;
      if (applyV2) body.filters_v2 = V2_ALL_ON;   // Phase 53.6 — opt-in v2
      const r = await axios.post(`${API}/vwap-bounce/backtest`, body);
      setResult(r.data);
      if (r.data.error) {
        toast.error(r.data.error);
      } else {
        toast.success(`VWAP: ${r.data.summary?.total_trades ?? 0} trades over ${r.data.days_scanned} days`);
      }
    } catch (e) {
      const msg = e?.response?.status === 429
        ? (e.response.data?.detail || "Rate limited — please wait.")
        : "Backtest failed";
      toast.error(msg);
    }
    finally { setRunning(false); }
  };

  const FIELDS = deriveFields(override);

  return (
    <div className="space-y-4" data-testid="bt-vwap">
      <div className="text-[10px] text-muted-3 font-mono-data">
        Replays 5-min candles across F&O equity universe (first 12 by default).<br />
        <strong>Setup A v3</strong> = VWAP Bounce Strategy V2.0 · 12-step Emergent
        Score (≥85/100 to fire). Steps: market trend, VWAP proximity (≤0.5%),
        pullback quality, bounce confirmation (body &gt;50% range · close upper 25%),
        vol ≥1.5× 20-avg, RSI 55-75, ADX≥25, 15-min HTF, R:R ≥ 2:1. Traps zero
        the score (gap &gt;2% · upper wick &gt;2×body · falling pre-bounce vol ·
        LH · bearish divergence · pre-bounce reds &gt;3 · entry after 14:45).<br />
        <strong>Setup B</strong> = LIMIT −{fmt(override?.buy_buffer_pct ?? 2)}%
        fill, v2 filter stack. <strong>Setup C</strong> = bearish PE, mirror v2.
        <br />
        <span className="text-muted-2">
          Historical backtest excludes symbols listed in Admin →
          Configuration → Section 8 → <code>setup_a_excluded_symbols</code>{" "}
          (Setup A only) and <code>excluded_symbols</code> (all setups).
        </span>
      </div>
      <label className="inline-flex items-center gap-2 text-[11px] font-mono-data cursor-pointer select-none"
             data-testid="vwap-bt-apply-v2-toggle">
        <input type="checkbox" checked={applyV2}
               onChange={(e) => setApplyV2(e.target.checked)}
               className="accent-white/80" />
        <span className={applyV2 ? "text-white" : "text-muted-3"}>
          Apply Setup B + C v2 filters (spec §3.1–3.7)
        </span>
        <span className="text-muted-3 text-[10px]">
          — {applyV2 ? "revised (strict — expect fewer trades)" : "baseline (v1)"}
        </span>
      </label>
      <BacktestControls fromDay={fromDay} toDay={toDay} setFromDay={setFromDay} setToDay={setToDay}
                         useOverride={useOverride} setUseOverride={setUseOverride}
                         override={override} setOverride={setOverride} fields={FIELDS}
                         run={run} running={running} />
      {result && (<>
        {result.error && (
          <div className="border p-3 font-mono-data text-xs" style={{ color: "var(--bear)", borderColor: "var(--bear)" }}>
            ⚠ {result.error}
          </div>
        )}
        <SummaryGrid summary={result.summary} />
        {result.by_setup && Object.keys(result.by_setup).length > 0 && (
          <div className="border border-grid p-3 font-mono-data text-[11px] flex flex-wrap gap-4">
            <span className="text-muted-3 uppercase tracking-widest">By Setup</span>
            {Object.entries(result.by_setup).map(([k, v]) => (
              <span key={k}>
                Setup {k}: <span className="text-muted-2">{v.trades}t · {v.wins}W · </span>
                <span style={{ color: v.pnl_pct >= 0 ? "var(--bull)" : "var(--bear)" }}>
                  {v.pnl_pct >= 0 ? "+" : ""}{fmt(v.pnl_pct)}%
                </span>
              </span>
            ))}
          </div>
        )}
        {((result.candle_fetch_failures ?? 0) > 0 || (result.daily_fetch_failures ?? 0) > 0) && (
          <div className="border p-2 font-mono-data text-[10px]"
               style={{ color: "#FFB66E", borderColor: "#FFB66E" }}
               title="Some symbol/day candle fetches failed even after retry — this run's trade set may differ from a re-run over the same range.">
            ⚠ {result.candle_fetch_failures ?? 0} intraday + {result.daily_fetch_failures ?? 0} daily
            candle fetches failed after retry — results may vary slightly on re-run.
          </div>
        )}
        {result.trades?.length ? <TradesTable trades={result.trades} showSymbol showSetup /> :
          !result.error && (
            <div className="text-[10px] text-muted-3 font-mono-data">
              No signals across {result.days_scanned} days × {result.symbols_scanned} symbols.
            </div>
          )}
      </>)}
    </div>
  );
};

const OIBacktest = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDay, setFromDay] = useState(new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10));
  const [toDay, setToDay] = useState(today);
  const [useOverride, setUseOverride] = useState(false);
  const [override, setOverride] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/oi-autopilot/defaults`).then((r) => {
      if (r.data?.defaults) setOverride({ ...r.data.defaults });
    }).catch(() => {
      setOverride({ rvol_threshold: 1.2, rvol_lookback: 10, target_pct: 10,
                    stop_pct: 15, max_entries_per_day: 2, break_lookback: 6 });
    });
  }, []);

  const run = async () => {
    setRunning(true); setResult(null);
    try {
      const body = { from_day: fromDay, to_day: toDay };
      if (useOverride) body.filters = override;
      const r = await axios.post(`${API}/oi-autopilot/backtest`, body);
      setResult(r.data);
      if (r.data.error) {
        toast.error(r.data.error);
      } else {
        toast.success(`OI: ${r.data.summary?.total_trades ?? 0} trades over ${r.data.days_scanned} days`);
      }
    } catch (e) {
      const msg = e?.response?.status === 429
        ? (e.response.data?.detail || "Rate limited — please wait.")
        : "Backtest failed";
      toast.error(msg);
    }
    finally { setRunning(false); }
  };

  const FIELDS = deriveFields(override);

  return (
    <div className="space-y-4" data-testid="bt-oi">
      <div className="text-[10px] text-muted-3 font-mono-data" style={{ color: "#FFB66E" }}>
        <strong>OI confirmation skipped</strong> — historical option-chain snapshots not stored.
        This is a spot-momentum proxy: intraday-high break + above-VWAP + rising EMA9 + RVOL. Fills the gap until
        Setup E&apos;s option-chain snapshots accumulate for a real OI backtest.
      </div>
      <BacktestControls fromDay={fromDay} toDay={toDay} setFromDay={setFromDay} setToDay={setToDay}
                         useOverride={useOverride} setUseOverride={setUseOverride}
                         override={override} setOverride={setOverride} fields={FIELDS}
                         run={run} running={running} />
      {result && (<>
        {result.error && (
          <div className="border p-3 font-mono-data text-xs" style={{ color: "var(--bear)", borderColor: "var(--bear)" }}>
            ⚠ {result.error}
          </div>
        )}
        <SummaryGrid summary={result.summary} />
        {result.trades?.length ? <TradesTable trades={result.trades} showSymbol /> :
          !result.error && (
            <div className="text-[10px] text-muted-3 font-mono-data">
              No signals across {result.days_scanned} days × {result.symbols_scanned} symbols.
            </div>
          )}
      </>)}
    </div>
  );
};

// --- Regime classifier & preset engine ---
// India VIX + Nifty-500 breadth ratio → market regime.
//   RISK_ON  = VIX < 14  AND breadth_ratio > 1.4  → loosen gates (more trades)
//   RISK_OFF = VIX > 18   OR breadth_ratio < 0.7  → tighten gates (fewer, higher-quality)
//   NEUTRAL  = anything else (also default when data missing)
const classifyRegime = ({ vix, breadth_ratio } = {}) => {
  const v = vix == null ? null : Number(vix);
  const b = breadth_ratio == null ? null : Number(breadth_ratio);
  if (v != null && b != null && v < 14 && b > 1.4) return "RISK_ON";
  if ((v != null && v > 18) || (b != null && b < 0.7)) return "RISK_OFF";
  return "NEUTRAL";
};

const REGIME_META = {
  RISK_ON: { label: "RISK ON", color: "var(--bull)",
              hint: "Low VIX, wide breadth — loosening gates to catch more moves." },
  NEUTRAL: { label: "NEUTRAL", color: "#FFB66E",
              hint: "Middle-of-the-road tape — default gates." },
  RISK_OFF: { label: "RISK OFF", color: "var(--bear)",
               hint: "High VIX or narrow breadth — tightening to only A+ setups." },
};

// --- Filter Tweaker (sliders with debounced live re-run) ---
// Renders a compact row of sliders for the strategy's most-impactful gates.
// Each slider change auto-triggers a debounced backtest run so the trade count
// updates ~400ms after the user releases the thumb — no "Run" button needed.
// Optional `regimePresets` = { RISK_ON: {...}, NEUTRAL: {...}, RISK_OFF: {...} }
// enables the "Apply Regime" one-click preset button.
const FilterTweaker = ({ tweakerGates, defaults, override, setOverride,
                          lastCount, running, onTweak, testIdPrefix = "tweaker",
                          regimePresets = null }) => {
  const [regime, setRegime] = useState(null);
  const [regimeFetching, setRegimeFetching] = useState(false);

  if (!defaults || !override) return null;
  const reset = () => {
    const next = { ...override };
    tweakerGates.forEach(([k]) => { next[k] = defaults[k]; });
    setOverride(next);
    onTweak(next);
  };

  const applyRegime = async () => {
    if (!regimePresets) return;
    setRegimeFetching(true);
    try {
      const r = await axios.get(`${API}/regime`);
      const label = classifyRegime(r.data || {});
      setRegime({ ...(r.data || {}), label });
      const preset = regimePresets[label] || regimePresets.NEUTRAL || {};
      const next = { ...override, ...preset };
      setOverride(next);
      onTweak(next);
      toast.success(`Regime · ${REGIME_META[label].label} preset applied`);
    } catch (e) {
      toast.error("Could not fetch regime snapshot");
    } finally { setRegimeFetching(false); }
  };

  return (
    <div className="border border-grid p-3 space-y-3" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 font-mono-data text-[11px] flex-wrap">
          <Sliders size={12} style={{ color: "var(--bull)" }} />
          <span className="uppercase tracking-widest text-muted-2">Filter Tweaker</span>
          <span className="text-muted-3 text-[10px]">
            slide any gate — trade count refreshes live
          </span>
          {regime && (
            <span data-testid={`${testIdPrefix}-regime-badge`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] uppercase tracking-widest"
                  style={{ color: REGIME_META[regime.label].color,
                            borderColor: REGIME_META[regime.label].color }}
                  title={`${REGIME_META[regime.label].hint}  · VIX=${regime.vix ?? "n/a"}  · Breadth=${regime.breadth_ratio ?? "unavailable"}`}>
              <Activity size={9} /> {REGIME_META[regime.label].label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 font-mono-data text-[11px]">
          {running ? (
            <span className="inline-flex items-center gap-1 text-muted-2">
              <RefreshCw size={11} className="animate-spin" /> scanning…
            </span>
          ) : (
            <span data-testid={`${testIdPrefix}-count`}>
              <span className="text-muted-3 uppercase text-[9px] tracking-widest mr-1">Trades</span>
              <span style={{ color: "var(--bull)" }} className="text-sm ml-1">{lastCount ?? "—"}</span>
            </span>
          )}
          {regimePresets && (
            <button onClick={applyRegime} disabled={regimeFetching}
                    data-testid={`${testIdPrefix}-regime`}
                    className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase hover:bg-white/[0.03]"
                    style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
                    title="Detect today's regime (VIX + breadth) and set every slider to the ideal preset.">
              {regimeFetching ? <RefreshCw size={10} className="animate-spin" />
                                : <Activity size={10} />}
              Apply Regime
            </button>
          )}
          <button onClick={reset} data-testid={`${testIdPrefix}-reset`}
                  className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase hover:bg-white/[0.03]"
                  style={{ color: "var(--text-muted-2)", borderColor: "var(--grid)" }}>
            <RotateCcw size={10} /> Reset
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tweakerGates.map(([k, label, min, max, step]) => {
          const val = Number(override[k] ?? defaults[k] ?? min);
          return (
            <div key={k} className="flex flex-col gap-1.5" data-testid={`${testIdPrefix}-${k}`}>
              <div className="flex items-center justify-between font-mono-data text-[11px]">
                <span className="text-muted-2">{label}</span>
                <span style={{ color: "var(--bull)" }} className="font-semibold">
                  {step >= 1 ? val : val.toFixed(2)}
                </span>
              </div>
              <Slider min={min} max={max} step={step} value={[val]}
                      onValueChange={([v]) => {
                        const next = { ...override, [k]: v };
                        setOverride(next);
                        onTweak(next);
                      }}
                      data-testid={`${testIdPrefix}-${k}-slider`} />
              <div className="flex justify-between text-[9px] text-muted-3 font-mono-data">
                <span>{step >= 1 ? min : min.toFixed(2)}</span>
                <span>{step >= 1 ? max : max.toFixed(2)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Shared debounce hook: fires cb 400ms after the last invocation.
const useDebouncedCallback = (cb, delay = 400) => {
  const timer = useRef(null);
  const cbRef = useRef(cb);
  useEffect(() => { cbRef.current = cb; }, [cb]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return useCallback((...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => cbRef.current(...args), delay);
  }, [delay]);
};


const BacktestControls = ({ fromDay, toDay, setFromDay, setToDay, useOverride, setUseOverride,
                            override, setOverride, fields, run, running }) => (
  <>
    <div className="flex items-center justify-between gap-3 flex-wrap font-mono-data text-xs">
      <div className="flex items-center gap-2">
        <span className="text-muted-3 uppercase text-[10px] tracking-widest">FROM</span>
        <input type="date" value={fromDay} onChange={(e) => setFromDay(e.target.value)}
                data-testid="bt-from" className="bg-transparent border border-grid px-2 py-1 text-xs" />
        <span className="text-muted-3 uppercase text-[10px] tracking-widest">TO</span>
        <input type="date" value={toDay} onChange={(e) => setToDay(e.target.value)}
                data-testid="bt-to" className="bg-transparent border border-grid px-2 py-1 text-xs" />
      </div>
      <button onClick={run} disabled={running} data-testid="bt-run"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 border text-xs uppercase font-mono-data hover:bg-white/[0.03]"
              style={{ color: "var(--bull)", borderColor: "var(--bull)" }}>
        {running ? <RefreshCw size={12} className="animate-spin" /> : <Play size={12} />}
        Run Backtest
      </button>
    </div>
    <div className="border border-grid p-3 space-y-2">
      <label className="flex items-center gap-2 font-mono-data text-[11px] cursor-pointer">
        <input type="checkbox" checked={useOverride}
                onChange={(e) => setUseOverride(e.target.checked)}
                data-testid="bt-use-override" />
        <span className="uppercase tracking-widest text-muted-2">What-if filter override</span>
        <span className="text-muted-3 text-[10px]">(temp — doesn&apos;t save)</span>
      </label>
      {useOverride && override && (
        <FilterEditor fields={fields} values={override}
                       onChange={(k, v) => setOverride((prev) => ({ ...prev, [k]: v }))} />
      )}
    </div>
  </>
);

// --- Section 10: VWAP+OI backtest ---

// Sliders exposed on the Filter Tweaker for VWAP+OI (most-impactful gates).
// Note: score_min is intentionally NOT exposed — V4.1 uses binary gates, not
// a weighted-score threshold. `max_entries_per_day` caps the per-day count.
const VWAP_OI_TWEAKER = [
  ["rvol_min", "RVOL Min", 0.5, 3.0, 0.05],
  ["price_vwap_max_pct", "Price ≤ VWAP+X%", 0.1, 3.0, 0.05],
  ["top_n", "Top-N Universe", 5, 50, 1],
  ["max_entries_per_day", "Max Entries / Day", 1, 10, 1],
];
// V4.1 has strict binary gates — even in RISK_ON we don't drop RVOL below 1.0.
const VWAP_OI_PRESETS = {
  RISK_ON:  { rvol_min: 1.0, price_vwap_max_pct: 1.0, top_n: 25, max_entries_per_day: 5 },
  NEUTRAL:  { rvol_min: 1.2, price_vwap_max_pct: 0.5, top_n: 15, max_entries_per_day: 3 },
  RISK_OFF: { rvol_min: 1.5, price_vwap_max_pct: 0.3, top_n: 10, max_entries_per_day: 2 },
};

// 2026-11-01 — tab hidden (setup retired); exported so it stays buildable without an unused-var warning.
export const VwapOIBacktest = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDay, setFromDay] = useState(new Date(Date.now() - 15 * 86400e3).toISOString().slice(0, 10));
  const [toDay, setToDay] = useState(today);
  const [useOverride, setUseOverride] = useState(false);
  const [override, setOverride] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/vwap-oi/defaults`).then((r) => {
      if (r.data?.defaults) {
        setOverride({ ...r.data.defaults });
        setDefaults({ ...r.data.defaults });
      }
    }).catch(() => {});
  }, []);

  const run = async (filtersToUse) => {
    setRunning(true);
    try {
      const body = { from_day: fromDay, to_day: toDay };
      const use = filtersToUse ?? (useOverride ? override : null);
      if (use) body.filters = use;
      const r = await axios.post(`${API}/vwap-oi/backtest`, body);
      setResult(r.data);
      if (r.data?.error) toast.error(r.data.error);
    } catch (e) {
      toast.error("Backtest failed");
    } finally { setRunning(false); }
  };

  const debouncedTweak = useDebouncedCallback((next) => { run(next); }, 500);

  const FIELDS = deriveFields(override);

  return (
    <div className="space-y-4" data-testid="bt-vwap-oi">
      <div className="text-[10px] text-muted-3 font-mono-data">
        Replays 5-min candles for the F&amp;O universe (~210 stocks; first 40 by default;
        loop ranks each day and evaluates only the top{" "}
        <strong>{fmt(override?.top_n ?? 15)}</strong> by momentum + relative
        strength).<br />
        <strong>VWAP + OI V4.0 (six-gate momentum long)</strong> — all six
        binary gates must pass: market above VWAP · stock above VWAP · 20 EMA
        &gt; 50 EMA · RVOL &gt; {fmt(override?.rvol_min ?? 1.2)} · volume
        increasing · price within{" "}
        <strong>{fmt(override?.price_vwap_max_pct ?? 0.5)}%</strong> of VWAP.
        Plus pullback confirmation (≥ 1 red candle in prior 3, current bar
        turns green above VWAP). Entry windows:{" "}
        <strong>09:20–10:30</strong> and <strong>13:30–14:30 IST</strong>.
        Removed: ADX, PCR, Fibonacci, OI, candle-body / wick rules.
      </div>
      <BacktestControls fromDay={fromDay} toDay={toDay} setFromDay={setFromDay} setToDay={setToDay}
                         useOverride={useOverride} setUseOverride={setUseOverride}
                         override={override} setOverride={setOverride} fields={FIELDS}
                         run={() => run()} running={running} />
      <FilterTweaker tweakerGates={VWAP_OI_TWEAKER} defaults={defaults}
                      override={override} setOverride={setOverride}
                      lastCount={result?.summary?.total_trades}
                      running={running} onTweak={debouncedTweak}
                      regimePresets={VWAP_OI_PRESETS}
                      testIdPrefix="tweaker-vwap-oi" />
      {result && (
        <>
          {result.error && (
            <div className="border p-3 font-mono-data text-xs"
                 style={{ color: "var(--bear)", borderColor: "var(--bear)" }}>
              ⚠ {result.error}
            </div>
          )}
          <SummaryGrid summary={result.summary} />
          {result.by_tier && Object.keys(result.by_tier).length > 0 && (
            <div className="border border-grid p-3 font-mono-data text-[11px] flex flex-wrap gap-4">
              <span className="text-muted-3 uppercase tracking-widest">By Tier</span>
              {Object.entries(result.by_tier).map(([k, v]) => (
                <span key={k}>
                  {k}: <span className="text-muted-2">{v.trades}t · {v.wins}W · </span>
                  <span style={{ color: v.pnl_pct >= 0 ? "var(--bull)" : "var(--bear)" }}>
                    {v.pnl_pct >= 0 ? "+" : ""}{fmt(v.pnl_pct)}%
                  </span>
                </span>
              ))}
            </div>
          )}
          {result.by_close_reason && Object.keys(result.by_close_reason).length > 0 && (
            <div className="border border-grid p-3 font-mono-data text-[11px] flex flex-wrap gap-4">
              <span className="text-muted-3 uppercase tracking-widest">By Exit Reason</span>
              {Object.entries(result.by_close_reason).map(([k, v]) => (
                <span key={k}>
                  {k}: <span className="text-muted-2">{v.trades}t · {v.wins}W · </span>
                  <span style={{ color: v.pnl_pct >= 0 ? "var(--bull)" : "var(--bear)" }}>
                    {v.pnl_pct >= 0 ? "+" : ""}{fmt(v.pnl_pct)}%
                  </span>
                </span>
              ))}
            </div>
          )}
          {result.trades?.length ? <TradesTable trades={result.trades} showSymbol /> :
            !result.error && (
              <div className="text-[10px] text-muted-3 font-mono-data space-y-1">
                <div>
                  No signals across {result.days_scanned} days × {result.symbols_scanned} symbols.
                  Consider lowering <code>score_min</code> in override.
                </div>
                {/* 2026-08-30 — chat: after fixing the universe to real F&O
                    names, a run came back with zero trades and no way to
                    tell whether that's a genuinely strict-gate result or a
                    data/resolution problem. The backend already computes
                    this gate-fail histogram (`_bt.run_backtest_range`'s
                    `debug` field) but it was silently dropped here — surface
                    it so a 0-trade run is diagnosable without DB access. */}
                {result.debug && (
                  <>
                    <div>
                      Symbols resolved: {result.debug.symbols_resolved ?? "—"}/{result.debug.symbols_total ?? "—"}
                      {" · "}Candle pairs with data: {result.debug.pairs_with_candles ?? "—"}/{result.debug.pairs_attempted ?? "—"}
                    </div>
                    {result.debug.sample_unresolved_symbols?.length > 0 && (
                      <div>
                        Unresolved symbols (sample): {result.debug.sample_unresolved_symbols.join(", ")}
                      </div>
                    )}
                    <div>
                      In-window evaluations: {result.debug.in_window_evals ?? 0}
                      {result.debug.gate_fail_counts &&
                        Object.keys(result.debug.gate_fail_counts).length > 0 && (
                          <>
                            {" — gate rejections: "}
                            {Object.entries(result.debug.gate_fail_counts)
                              .sort((a, b) => b[1] - a[1])
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(", ")}
                          </>
                        )}
                    </div>
                  </>
                )}
              </div>
            )}
        </>
      )}
    </div>
  );
};

// --- Section 11: ORB backtest ---

const ORB_TWEAKER = [
  ["range_minutes", "Opening Range (min)", 5, 60, 5],
  ["min_rvol", "RVOL Min", 0.5, 3.0, 0.05],
  ["min_rr_ratio", "Reward/Risk Min", 0.5, 3.0, 0.05],
  ["range_target_mult", "Target × Range", 0.5, 3.0, 0.05],
];
const ORB_PRESETS = {
  // RISK_ON: shorter range + looser RVOL + wider target for trending days
  RISK_ON:  { range_minutes: 15, min_rvol: 1.2, min_rr_ratio: 1.3, range_target_mult: 2.0 },
  NEUTRAL:  { range_minutes: 15, min_rvol: 1.5, min_rr_ratio: 1.5, range_target_mult: 1.5 },
  // RISK_OFF: longer range (fewer false breakouts) + strict RVOL/RR
  RISK_OFF: { range_minutes: 30, min_rvol: 2.0, min_rr_ratio: 2.0, range_target_mult: 1.5 },
};

// 2026-11-01 — tab hidden (setup retired); exported so it stays buildable without an unused-var warning.
export const OrbBacktest = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDay, setFromDay] = useState(new Date(Date.now() - 15 * 86400e3).toISOString().slice(0, 10));
  const [toDay, setToDay] = useState(today);
  const [useOverride, setUseOverride] = useState(false);
  const [override, setOverride] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/orb/defaults`).then((r) => {
      if (r.data?.defaults) {
        setOverride({ ...r.data.defaults });
        setDefaults({ ...r.data.defaults });
      }
    }).catch(() => {});
  }, []);

  const run = async (filtersToUse) => {
    setRunning(true);
    try {
      const body = { from_day: fromDay, to_day: toDay };
      const use = filtersToUse ?? (useOverride ? override : null);
      if (use) body.filters = use;
      const r = await axios.post(`${API}/orb/backtest`, body);
      setResult(r.data);
      if (r.data?.error) toast.error(r.data.error);
    } catch (e) {
      toast.error("ORB backtest failed");
    } finally { setRunning(false); }
  };

  const debouncedTweak = useDebouncedCallback((next) => { run(next); }, 500);

  const FIELDS = deriveFields(override);

  return (
    <div className="space-y-4" data-testid="bt-orb">
      <div className="text-[10px] text-muted-3 font-mono-data">
        <strong>Opening Range Breakout</strong> — marks the first{" "}
        <strong>{fmt(override?.range_minutes ?? 15, 0)} min</strong> high/low as
        the &quot;opening range&quot;. Enters on a 5-min close that breaks outside the
        range with RVOL ≥ <strong>{fmt(override?.min_rvol ?? 1.5)}</strong>.
        SL = opposite side of range · target ={" "}
        <strong>{fmt(override?.range_target_mult ?? 1.5)}×</strong> range width
        (min R/R <strong>{fmt(override?.min_rr_ratio ?? 1.5)}</strong>). EOD
        flat 15:15 IST. Shorts{" "}
        {override?.shorts_enabled ? "enabled" : "disabled"}.
      </div>
      <BacktestControls fromDay={fromDay} toDay={toDay} setFromDay={setFromDay} setToDay={setToDay}
                         useOverride={useOverride} setUseOverride={setUseOverride}
                         override={override} setOverride={setOverride} fields={FIELDS}
                         run={() => run()} running={running} />
      <FilterTweaker tweakerGates={ORB_TWEAKER} defaults={defaults}
                      override={override} setOverride={setOverride}
                      lastCount={result?.summary?.total_trades}
                      running={running} onTweak={debouncedTweak}
                      regimePresets={ORB_PRESETS}
                      testIdPrefix="tweaker-orb" />
      {result && (
        <>
          {result.error && (
            <div className="border p-3 font-mono-data text-xs"
                 style={{ color: "var(--bear)", borderColor: "var(--bear)" }}>
              ⚠ {result.error}
            </div>
          )}
          <SummaryGrid summary={result.summary} />
          {result.trades?.length ? <TradesTable trades={result.trades} showSymbol /> :
            !result.error && (
              <div className="text-[10px] text-muted-3 font-mono-data">
                No ORB breakouts across {result.days_scanned} days ×{" "}
                {result.symbols_scanned} symbols. Try lowering{" "}
                <code>min_rvol</code> or shrinking the opening range.
              </div>
            )}
        </>
      )}
    </div>
  );
};

// --- Section 12: Gap-and-Go backtest ---

const GAP_TWEAKER = [
  ["min_gap_pct", "Gap % Min", 0.2, 5.0, 0.05],
  ["min_rvol", "RVOL Min", 0.5, 3.0, 0.05],
  ["target_mult", "Target × Bar Range", 0.5, 3.0, 0.05],
  ["top_n", "Top-N Universe", 5, 50, 1],
];
const GAP_PRESETS = {
  // RISK_ON: catch more gap-ups (lower gap floor + wider target)
  RISK_ON:  { min_gap_pct: 0.7, min_rvol: 1.0, target_mult: 2.0, top_n: 30 },
  NEUTRAL:  { min_gap_pct: 1.0, min_rvol: 1.2, target_mult: 1.5, top_n: 20 },
  // RISK_OFF: only large, strong gap-ups on days when the tape is defensive
  RISK_OFF: { min_gap_pct: 1.5, min_rvol: 1.5, target_mult: 1.5, top_n: 15 },
};

// 2026-11-01 — tab hidden (setup retired); exported so it stays buildable without an unused-var warning.
export const GapAndGoBacktest = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDay, setFromDay] = useState(new Date(Date.now() - 15 * 86400e3).toISOString().slice(0, 10));
  const [toDay, setToDay] = useState(today);
  const [useOverride, setUseOverride] = useState(false);
  const [override, setOverride] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gap/defaults`).then((r) => {
      if (r.data?.defaults) {
        setOverride({ ...r.data.defaults });
        setDefaults({ ...r.data.defaults });
      }
    }).catch(() => {});
  }, []);

  const run = async (filtersToUse) => {
    setRunning(true);
    try {
      const body = { from_day: fromDay, to_day: toDay };
      const use = filtersToUse ?? (useOverride ? override : null);
      if (use) body.filters = use;
      const r = await axios.post(`${API}/gap/backtest`, body);
      setResult(r.data);
      if (r.data?.error) toast.error(r.data.error);
    } catch (e) {
      toast.error("Gap-and-Go backtest failed");
    } finally { setRunning(false); }
  };

  const debouncedTweak = useDebouncedCallback((next) => { run(next); }, 500);

  const FIELDS = deriveFields(override);

  return (
    <div className="space-y-4" data-testid="bt-gap">
      <div className="text-[10px] text-muted-3 font-mono-data">
        <strong>Gap-and-Go</strong> — enters LONG at the close of the first
        9:15–9:20 bar if: (1) stock gaps up{" "}
        ≥ <strong>{fmt(override?.min_gap_pct ?? 1.0)}%</strong> vs prior close,
        (2) first bar is green and closes above prior close,
        (3) RVOL ≥ <strong>{fmt(override?.min_rvol ?? 1.2)}</strong>.
        SL = first-bar low · target ={" "}
        <strong>{fmt(override?.target_mult ?? 1.5)}×</strong> first-bar range.
        EOD flat 15:15 IST.
      </div>
      <BacktestControls fromDay={fromDay} toDay={toDay} setFromDay={setFromDay} setToDay={setToDay}
                         useOverride={useOverride} setUseOverride={setUseOverride}
                         override={override} setOverride={setOverride} fields={FIELDS}
                         run={() => run()} running={running} />
      <FilterTweaker tweakerGates={GAP_TWEAKER} defaults={defaults}
                      override={override} setOverride={setOverride}
                      lastCount={result?.summary?.total_trades}
                      running={running} onTweak={debouncedTweak}
                      regimePresets={GAP_PRESETS}
                      testIdPrefix="tweaker-gap" />
      {result && (
        <>
          {result.error && (
            <div className="border p-3 font-mono-data text-xs"
                 style={{ color: "var(--bear)", borderColor: "var(--bear)" }}>
              ⚠ {result.error}
            </div>
          )}
          <SummaryGrid summary={result.summary} />
          {result.trades?.length ? <TradesTable trades={result.trades} showSymbol /> :
            !result.error && (
              <div className="text-[10px] text-muted-3 font-mono-data">
                No qualifying gap-ups across {result.days_scanned} days ×{" "}
                {result.symbols_scanned} symbols. Try lowering{" "}
                <code>min_gap_pct</code> or <code>min_rvol</code>.
              </div>
            )}
        </>
      )}
    </div>
  );
};

const MOMENTUM_TWEAKER = [
  ["score_alert_threshold",       "Score Min",           40,  100, 1],
  ["price_change_threshold_pct",  "Price Δ % Min",       0.2,  5.0, 0.1],
  ["volume_multiplier",           "Volume Multiplier",   1.0,  5.0, 0.1],
  ["adx_threshold",               "ADX Min",             10,   50, 1],
];
// Preset tables reuse the same regime buckets as the other four strategies.
const MOMENTUM_PRESETS = {
  RISK_ON:  { score_alert_threshold: 65, price_change_threshold_pct: 1.0,
              volume_multiplier: 2.0, adx_threshold: 20 },
  NEUTRAL:  { score_alert_threshold: 75, price_change_threshold_pct: 1.5,
              volume_multiplier: 2.5, adx_threshold: 25 },
  RISK_OFF: { score_alert_threshold: 80, price_change_threshold_pct: 2.0,
              volume_multiplier: 3.0, adx_threshold: 30 },
};

const MomentumBacktest = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDay, setFromDay] = useState(new Date(Date.now() - 8 * 86400e3).toISOString().slice(0, 10));
  const [toDay, setToDay] = useState(today);
  const [useOverride, setUseOverride] = useState(false);
  const [override, setOverride] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [universeTopN, setUniverseTopN] = useState(20);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/momentum/defaults`).then((r) => {
      if (r.data?.defaults) {
        setOverride({ ...r.data.defaults });
        setDefaults({ ...r.data.defaults });
      }
    }).catch(() => {});
  }, []);

  const run = async (filtersToUse) => {
    setRunning(true);
    try {
      const body = { from_day: fromDay, to_day: toDay, universe_top_n: universeTopN };
      const use = filtersToUse ?? (useOverride ? override : null);
      if (use) body.filters = use;
      const r = await axios.post(`${API}/momentum/backtest`, body);
      setResult(r.data);
      if (r.data?.error) toast.error(r.data.error);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Momentum backtest failed");
    } finally { setRunning(false); }
  };

  const debouncedTweak = useDebouncedCallback((next) => { run(next); }, 700);
  const FIELDS = deriveFields(override);

  return (
    <div className="space-y-4" data-testid="bt-momentum">
      <div className="text-[10px] text-muted-3 font-mono-data">
        <strong>Momentum Ignition</strong> — replays the 8-gate scoring engine
        (Price · OI · Volume · VWAP · EMA20 · ADX · RSI · 15-min Breakout)
        used by the live 30-second scanner over historical 5-min candles.
        Signals ≥ <strong>{fmt(override?.score_alert_threshold ?? 75, 0)}</strong>{" "}
        become paper trades: ₹{fmt(override?.paper_capital_per_trade ?? 50000, 0)} notional
        on the underlying stock, exit via target/SL/trail/time/VWAP-cross/EOD.
        OI change is soft-passed (option-chain history not fetched — same
        behaviour as the live sidecar cold-start).
      </div>
      <div className="flex items-center gap-2 flex-wrap font-mono-data text-xs">
        <span className="text-muted-3 uppercase text-[10px] tracking-widest">Universe Top-N</span>
        <input type="number" min={5} max={100} step={5}
                value={universeTopN} onChange={(e) => setUniverseTopN(parseInt(e.target.value, 10) || 20)}
                data-testid="bt-momentum-universe" className="bg-transparent border border-grid px-2 py-1 text-xs w-16" />
        <span className="text-muted-3 text-[10px]">
          (fewer symbols = faster runs; 20 ≈ 60s per week)
        </span>
      </div>
      <BacktestControls fromDay={fromDay} toDay={toDay} setFromDay={setFromDay} setToDay={setToDay}
                         useOverride={useOverride} setUseOverride={setUseOverride}
                         override={override} setOverride={setOverride} fields={FIELDS}
                         run={() => run()} running={running} />
      <FilterTweaker tweakerGates={MOMENTUM_TWEAKER} defaults={defaults}
                      override={override} setOverride={setOverride}
                      lastCount={result?.summary?.total_trades}
                      running={running} onTweak={debouncedTweak}
                      regimePresets={MOMENTUM_PRESETS}
                      testIdPrefix="tweaker-momentum" />
      {result && (
        <>
          {result.error && (
            <div className="border p-3 font-mono-data text-xs"
                 style={{ color: "var(--bear)", borderColor: "var(--bear)" }}>
              ⚠ {result.error}
            </div>
          )}
          <SummaryGrid summary={result.summary} />
          {result.by_close_reason && Object.keys(result.by_close_reason).length > 0 && (
            <div className="border border-grid p-3 font-mono-data text-[11px] flex flex-wrap gap-4">
              <span className="text-muted-3 uppercase tracking-widest">Exit Reasons</span>
              {Object.entries(result.by_close_reason).map(([k, v]) => (
                <span key={k}>
                  {k}: <span style={{ color: "var(--bull)" }}>{v}</span>
                </span>
              ))}
            </div>
          )}
          {result.trades?.length ? <TradesTable trades={result.trades} showSymbol /> :
            !result.error && (
              <div className="text-[10px] text-muted-3 font-mono-data">
                No qualifying momentum signals across {result.days_scanned} days ×{" "}
                {result.symbols_scanned} symbols. Try lowering{" "}
                <code>score_alert_threshold</code> or{" "}
                <code>price_change_threshold_pct</code>.
              </div>
            )}
          {result.pessimistic_intrabar_exit && (result.trades?.length || 0) > 0 && (
            <div className="text-[10px] text-muted-3 font-mono-data">
              ℹ Intrabar exit is <strong>pessimistic</strong> — when both
              stop-loss and target fall inside the same 5-min candle, we assume
              stop-loss hits first (long) / target-first (short). Results are
              therefore a conservative floor; live behaviour tends to be
              slightly better.
            </div>
          )}
        </>
      )}
    </div>
  );
};


// --- Section 5: Candle Breakout backtest (2026-09-12) ---

const CandleBreakoutBacktest = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDay, setFromDay] = useState(new Date(Date.now() - 15 * 86400e3).toISOString().slice(0, 10));
  const [toDay, setToDay] = useState(today);
  const [useOverride, setUseOverride] = useState(false);
  const [override, setOverride] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/candle-breakout/defaults`).then((r) => {
      if (r.data?.defaults) setOverride({ ...r.data.defaults });
    }).catch(() => {
      setOverride({ target_pct: 8.5, stop_pct: 10.0, max_entries_per_day: 2,
                    no_new_entry_hh: 14, no_new_entry_mm: 0,
                    supertrend_period: 7, supertrend_multiplier: 3.0,
                    rsi_min: 50.0, min_anticipated_pct: 8.0 });
    });
  }, []);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  useEffect(() => () => stopPolling(), []);

  // 2026-09-12 — this endpoint now always dispatches as a background job
  // (see server.py's /candle-breakout/backtest docstring): the first
  // synchronous run hit Upstox 429s, and even after fixing those the added
  // retry/pacing made a multi-day run a proxy-timeout ("504") risk too.
  // Polling job_engine sidesteps the proxy's clock entirely — same pattern
  // LinearAccumulation.jsx already uses for its own long-running backtests.
  const startPolling = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/jobs/${jobId}`);
        setJob(data);
        if (["success", "failed", "cancelled"].includes(data?.status)) {
          stopPolling();
          setRunning(false);
          if (data.status === "success") {
            const r = data.result || {};
            setResult(r);
            if (r.error) {
              toast.error(r.error);
            } else {
              toast.success(`CB: ${r.summary?.total_trades ?? 0} trades over ${r.days_scanned} days`);
            }
          } else if (data.status === "failed") {
            const msg = data.error || "Backtest failed";
            toast.error(msg);
            setResult({ error: msg, summary: null, trades: [] });
          } else {
            toast.info("Backtest cancelled");
          }
        }
      } catch { /* transient polling error is fine — retry next tick */ }
    }, 2000);
  };

  const cancelJob = async () => {
    if (!job?.job_id) return;
    try {
      await axios.post(`${API}/jobs/${job.job_id}/cancel`);
      toast.info("Cancellation requested…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const run = async () => {
    setRunning(true); setResult(null); setJob(null);
    try {
      const body = { from_day: fromDay, to_day: toDay };
      if (useOverride) body.filters = override;
      const r = await axios.post(`${API}/candle-breakout/backtest`, body);
      if (r.data?.mode === "async") {
        setJob(r.data.job);
        startPolling(r.data.job_id);
      } else {
        // Defensive fallback in case the endpoint ever responds inline.
        setResult(r.data);
        setRunning(false);
      }
    } catch (e) {
      // 2026-09-12 — was a bare "Backtest failed" with no way to tell what
      // broke on a brand-new, unverified endpoint. Surface whatever detail
      // the backend actually sent back.
      const msg = e?.response?.data?.detail || e?.response?.data?.error
        || (e?.response?.status === 429 ? "Rate limited — please wait." : null)
        || e?.message || "Backtest failed";
      toast.error(msg);
      setResult({ error: msg, summary: null, trades: [] });
      setRunning(false);
    }
  };

  const FIELDS = deriveFields(override);

  return (
    <div className="space-y-4" data-testid="bt-candle-breakout">
      <div className="text-[10px] text-muted-3 font-mono-data">
        Replays the live 5-rule Chartink-style breakout (native_breakout_scanner.
        evaluate_first_2_breakout — same function the live scan calls) over the{" "}
        <strong>Nifty 100</strong> universe: 1st 5-min candle green, 2nd gaps up
        above it, current close breaks 2nd candle&apos;s high, daily Supertrend(7,3)
        bullish, above daily VWAP — identical to live. A second gate then
        applies, same as live&apos;s auto-entry pipeline: 5m + 1h + 1d trend must
        all agree bullish, a 2nd Supertrend/RSI check
        (period {fmt(override?.supertrend_period ?? 7, 0)}, RSI ≥ {fmt(override?.rsi_min ?? 50, 0)}),
        and anticipated profit ≥ {fmt(override?.min_anticipated_pct ?? 8, 0)}%.{" "}
        <span className="text-muted-2">
          That second gate is approximated with Upstox candles instead of
          live&apos;s yfinance-based check (2026-09-12 — chat: &quot;CB starts
          around 9:45...most of them are around 9:25&quot;, since the original
          version skipped this gate entirely). Target/SL still use a static
          base band ({fmt(override?.target_pct ?? 8.5)}% /
          −{fmt(override?.stop_pct ?? 10.0)}%), not live&apos;s dynamic sector/
          sentiment/ATR/multi-timeframe adjustment (not stored historically).
          Runs as a background job — progress below.
        </span>
      </div>
      <BacktestControls fromDay={fromDay} toDay={toDay} setFromDay={setFromDay} setToDay={setToDay}
                         useOverride={useOverride} setUseOverride={setUseOverride}
                         override={override} setOverride={setOverride} fields={FIELDS}
                         run={run} running={running} />
      <JobProgress job={job} onCancel={cancelJob} testPrefix="cb-backtest" />
      {result && (<>
        {result.error && (
          <div className="border p-3 font-mono-data text-xs" style={{ color: "var(--bear)", borderColor: "var(--bear)" }}>
            ⚠ {result.error}
          </div>
        )}
        <SummaryGrid summary={result.summary} />
        {((result.candle_fetch_failures ?? 0) > 0 || (result.hourly_fetch_failures ?? 0) > 0
          || (result.daily_fetch_failures ?? 0) > 0) && (
          <div className="border p-2 font-mono-data text-[10px]"
               style={{ color: "#FFB66E", borderColor: "#FFB66E" }}>
            ⚠ {result.candle_fetch_failures ?? 0} 5-min + {result.hourly_fetch_failures ?? 0} hourly +{" "}
            {result.daily_fetch_failures ?? 0} daily candle fetches failed after retry — results may
            vary slightly on re-run.
          </div>
        )}
        {(result.stage2_rejections ?? 0) > 0 && (
          <div className="text-[10px] text-muted-3 font-mono-data">
            {result.stage2_rejections} raw pattern match{result.stage2_rejections === 1 ? "" : "es"} rejected
            by the stage-2 gate (MTF/ST/RSI/anticipated-profit) — see description above.
          </div>
        )}
        {result.trades?.length ? <TradesTable trades={result.trades} showSymbol /> :
          !result.error && (
            <div className="text-[10px] text-muted-3 font-mono-data">
              No breakouts across {result.days_scanned} days × {result.symbols_scanned} symbols.
            </div>
          )}
      </>)}
    </div>
  );
};

// --- Section 4: Sniper Setups backtest (2026-09-12) ---

const SniperBacktest = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDay, setFromDay] = useState(new Date(Date.now() - 15 * 86400e3).toISOString().slice(0, 10));
  const [toDay, setToDay] = useState(today);
  const [useOverride, setUseOverride] = useState(false);
  const [override, setOverride] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/sniper/defaults`).then((r) => {
      if (r.data?.defaults) setOverride({ ...r.data.defaults });
    }).catch(() => {
      setOverride({ min_score: 80, min_risk_reward: 2.0, price_change_band_pct: 1.0,
                    target_pct: 7.0, stop_loss_pct: -6.0, max_auto_picks_per_day: 8,
                    min_rvol: 1.5, adx_trend_floor: 20.0 });
    });
  }, []);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  useEffect(() => () => stopPolling(), []);

  // 2026-09-12 — same background-job dispatch as Candle Breakout above
  // (see server.py's /sniper/backtest docstring) to avoid the reverse
  // proxy's ~60s timeout on longer ranges.
  const startPolling = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/jobs/${jobId}`);
        setJob(data);
        if (["success", "failed", "cancelled"].includes(data?.status)) {
          stopPolling();
          setRunning(false);
          if (data.status === "success") {
            const r = data.result || {};
            setResult(r);
            if (r.error) {
              toast.error(r.error);
            } else {
              toast.success(`Sniper: ${r.summary?.total_trades ?? 0} trades over ${r.days_scanned} days`);
            }
          } else if (data.status === "failed") {
            const msg = data.error || "Backtest failed";
            toast.error(msg);
            setResult({ error: msg, summary: null, trades: [] });
          } else {
            toast.info("Backtest cancelled");
          }
        }
      } catch { /* transient polling error is fine — retry next tick */ }
    }, 2000);
  };

  const cancelJob = async () => {
    if (!job?.job_id) return;
    try {
      await axios.post(`${API}/jobs/${job.job_id}/cancel`);
      toast.info("Cancellation requested…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const run = async () => {
    setRunning(true); setResult(null); setJob(null);
    try {
      const body = { from_day: fromDay, to_day: toDay };
      if (useOverride) body.filters = override;
      const r = await axios.post(`${API}/sniper/backtest`, body);
      if (r.data?.mode === "async") {
        setJob(r.data.job);
        startPolling(r.data.job_id);
      } else {
        setResult(r.data);
        setRunning(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error
        || (e?.response?.status === 429 ? "Rate limited — please wait." : null)
        || e?.message || "Backtest failed";
      toast.error(msg);
      setResult({ error: msg, summary: null, trades: [] });
      setRunning(false);
    }
  };

  const FIELDS = deriveFields(override);

  return (
    <div className="space-y-4" data-testid="bt-sniper">
      <div className="text-[10px] text-muted-3 font-mono-data">
        Replays the live entry stack over the <strong>Nifty 100</strong> universe on
        15-min candles: <code>analyze_stock()</code> (VWAP/EMA9-21 trend, RSI/MACD
        momentum, breakout + volume-spike scoring) feeding the SAME hard-gate
        stack live auto-entries use — score ≥ {fmt(override?.min_score ?? 80, 0)},
        R:R ≥ {fmt(override?.min_risk_reward ?? 2.0)}, RVOL ≥ {fmt(override?.min_rvol ?? 1.5)},
        ADX ≥ {fmt(override?.adx_trend_floor ?? 20, 0)}, directional RSI bands, and the
        12:45–13:45 low-conviction RVOL window — including any admin overrides
        currently saved under Configuration → Section 4. Target/SL are the same
        option-premium proxy every backtest in this app uses. Runs as a
        background job — progress below.
      </div>
      <BacktestControls fromDay={fromDay} toDay={toDay} setFromDay={setFromDay} setToDay={setToDay}
                         useOverride={useOverride} setUseOverride={setUseOverride}
                         override={override} setOverride={setOverride} fields={FIELDS}
                         run={run} running={running} />
      <JobProgress job={job} onCancel={cancelJob} testPrefix="sniper-backtest" />
      {result && (<>
        {result.error && (
          <div className="border p-3 font-mono-data text-xs" style={{ color: "var(--bear)", borderColor: "var(--bear)" }}>
            ⚠ {result.error}
          </div>
        )}
        <SummaryGrid summary={result.summary} />
        {(result.candle_fetch_failures ?? 0) > 0 && (
          <div className="border p-2 font-mono-data text-[10px]"
               style={{ color: "#FFB66E", borderColor: "#FFB66E" }}>
            ⚠ {result.candle_fetch_failures} candle fetches failed after retry — results
            may vary slightly on re-run.
          </div>
        )}
        {result.trades?.length ? <TradesTable trades={result.trades} showSymbol /> :
          !result.error && (
            <div className="text-[10px] text-muted-3 font-mono-data">
              No qualifying setups across {result.days_scanned} days × {result.symbols_scanned} symbols.
            </div>
          )}
      </>)}
    </div>
  );
};

// --- Main Backtests page ---

export const BacktestsPage = () => {
  const [tab, setTab] = useState("setup-e");
  const tabs = [
    { id: "setup-e", label: "Setup E V2 · Expiry Momentum" },
    { id: "vwap", label: "VWAP Bounce · A/B/C" },
    // 2026-11-01 — VWAP+OI / ORB / Gap-and-Go tabs hidden: setups retired (Journal → Stock lab).
    { id: "momentum", label: "Momentum Ignition" },
    { id: "oi", label: "OI Long Buildup" },
    { id: "candle-breakout", label: "Candle Breakout" },
    { id: "sniper", label: "Sniper Setups" },
  ];
  return (
    <section className="space-y-6" data-testid="backtests-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
          Auto-Pilot · Historical Simulator
        </div>
        <h2 className="font-heading text-2xl flex items-center gap-2">
          <TestTube size={20} /> Backtests
        </h2>
        <p className="text-xs text-muted-2 mt-1">
          Replay historical Upstox candles against each setup&apos;s entry logic. All backtests support inline
          &quot;What-if&quot; filter overrides — tune thresholds, hit Run, iterate. Live scanner is unaffected.
        </p>
      </div>

      <div className="border-b border-grid flex gap-1" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} role="tab"
                  aria-selected={tab === t.id}
                  data-testid={`bt-tab-${t.id}`}
                  className="px-4 py-2 text-xs uppercase tracking-widest font-mono-data border-b-2"
                  style={{
                    borderColor: tab === t.id ? "var(--bull)" : "transparent",
                    color: tab === t.id ? "var(--bull)" : "var(--text-muted-2)",
                  }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="border border-grid p-5">
        {tab === "setup-e" && <SetupEBacktest />}
        {tab === "vwap" && <VwapBounceBacktest />}
        {tab === "momentum" && <MomentumBacktest />}
        {tab === "oi" && <OIBacktest />}
        {tab === "candle-breakout" && <CandleBreakoutBacktest />}
        {tab === "sniper" && <SniperBacktest />}
      </div>
    </section>
  );
};
